import { forwardRef, useId } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
  useMonkeysLocaleMessages,
} from '../../provider/context';
import { resolveBaseAppearance } from './theme';
import type {
  BaseStepItem,
  BaseStepsProps,
  BaseStepsStatusLabels,
  BaseStepStatus,
} from './types';
import { cn, hasRenderableNode } from './utils';

const stepStatuses = new Set<BaseStepStatus>([
  'pending',
  'current',
  'completed',
  'error',
]);
const statusLabelKeys = [
  'current',
  'completed',
  'error',
  'disabled',
] as const;
const invalidStepIdPattern = /[\s\u0000-\u001f\u007f]/u;

interface ValidatedStep {
  item: BaseStepItem;
  encodedId: string;
  status: BaseStepStatus;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasStepLabel(value: unknown): boolean {
  if (!hasRenderableNode(value as BaseStepItem['label'])) return false;
  if (typeof value === 'string') return Boolean(value.trim());
  if (Array.isArray(value)) return value.some(hasStepLabel);
  return true;
}

function encodeStepId(id: string) {
  try {
    return encodeURIComponent(id);
  } catch {
    throw new TypeError(
      `BaseSteps item id ${JSON.stringify(id)} must contain valid Unicode.`,
    );
  }
}

function validateItems(items: unknown): ValidatedStep[] {
  if (!Array.isArray(items)) {
    throw new TypeError('BaseSteps items must be an array.');
  }

  const ids = new Set<string>();
  let currentCount = 0;

  const validatedItems = items.map((candidate, index) => {
    if (!isPlainRecord(candidate)) {
      throw new TypeError(`BaseSteps item at index ${index} must be an object.`);
    }

    const item = candidate as unknown as BaseStepItem;
    if (
      typeof item.id !== 'string' ||
      !item.id ||
      invalidStepIdPattern.test(item.id)
    ) {
      throw new TypeError(
        `BaseSteps item at index ${index} must have a non-empty id without whitespace or control characters.`,
      );
    }
    if (ids.has(item.id)) {
      throw new TypeError(`BaseSteps item id ${JSON.stringify(item.id)} is duplicated.`);
    }
    ids.add(item.id);

    if (!hasStepLabel(item.label)) {
      throw new TypeError(
        `BaseSteps item ${JSON.stringify(item.id)} label must contain renderable content.`,
      );
    }

    const status = item.status ?? 'pending';
    if (!stepStatuses.has(status)) {
      throw new TypeError(
        `BaseSteps item ${JSON.stringify(item.id)} status must be one of: pending, current, completed, error.`,
      );
    }
    if (status === 'current') currentCount += 1;

    if (item.disabled !== undefined && typeof item.disabled !== 'boolean') {
      throw new TypeError(
        `BaseSteps item ${JSON.stringify(item.id)} disabled must be a boolean.`,
      );
    }
    if (item.selectable !== undefined && typeof item.selectable !== 'boolean') {
      throw new TypeError(
        `BaseSteps item ${JSON.stringify(item.id)} selectable must be a boolean.`,
      );
    }

    return { item, encodedId: encodeStepId(item.id), status };
  });

  if (currentCount > 1) {
    throw new RangeError('BaseSteps items may contain at most one current step.');
  }

  return validatedItems;
}

function resolveStatusLabels(
  defaults: BaseStepsStatusLabels,
  override: unknown,
): BaseStepsStatusLabels {
  if (override !== undefined && !isPlainRecord(override)) {
    throw new TypeError('BaseSteps statusLabels must be an object.');
  }

  const overrideRecord = override as Record<string, unknown> | undefined;
  for (const key of Object.keys(overrideRecord ?? {})) {
    if (!statusLabelKeys.includes(key as (typeof statusLabelKeys)[number])) {
      throw new TypeError(
        `BaseSteps statusLabels.${key} is not a supported status label.`,
      );
    }
  }

  const resolved = {
    current: overrideRecord?.current ?? defaults.current,
    completed: overrideRecord?.completed ?? defaults.completed,
    error: overrideRecord?.error ?? defaults.error,
    disabled: overrideRecord?.disabled ?? defaults.disabled,
  };

  for (const key of statusLabelKeys) {
    const value = resolved[key];
    if (typeof value !== 'string' || !value.trim()) {
      throw new TypeError(
        `BaseSteps statusLabels.${key} must be a non-empty string.`,
      );
    }
  }

  return resolved as BaseStepsStatusLabels;
}

function validateAccessibleName(
  ariaLabel: unknown,
  ariaLabelledBy: unknown,
) {
  if (
    ariaLabel !== undefined &&
    (typeof ariaLabel !== 'string' || !ariaLabel.trim())
  ) {
    throw new TypeError(
      'BaseSteps aria-label must be a non-empty string when provided.',
    );
  }
  if (ariaLabelledBy === undefined) return;
  if (typeof ariaLabelledBy !== 'string' || !ariaLabelledBy.trim()) {
    throw new TypeError(
      'BaseSteps aria-labelledby must contain at least one valid ID token.',
    );
  }
  const tokens = ariaLabelledBy.trim().split(/\s+/);
  if (tokens.some((token) => invalidStepIdPattern.test(token))) {
    throw new TypeError(
      'BaseSteps aria-labelledby must contain only valid ID tokens.',
    );
  }
}

function getIndicator(status: BaseStepStatus, index: number) {
  if (status === 'completed') return '\u2713';
  if (status === 'error') return '!';
  return index + 1;
}

function getIndicatorClassName(
  status: BaseStepStatus,
  slots: ReturnType<typeof resolveBaseAppearance>['slots'],
) {
  return cn(
    status === 'pending' && slots.stepsIndicatorPending,
    status === 'current' && slots.stepsIndicatorCurrent,
    status === 'completed' && slots.stepsIndicatorCompleted,
    status === 'error' && slots.stepsIndicatorError,
  );
}

export const BaseSteps = forwardRef<HTMLOListElement, BaseStepsProps>(
  function BaseSteps(
    {
      items,
      orientation = 'horizontal',
      statusLabels,
      appearance,
      className,
      classNames,
      style,
      onStepSelect,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...stepsProps
    },
    ref,
  ) {
    const generatedId = useId();
    const messages = useMonkeysLocaleMessages().steps;
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);

    if (orientation !== 'horizontal' && orientation !== 'vertical') {
      throw new TypeError(
        'BaseSteps orientation must be "horizontal" or "vertical".',
      );
    }
    if (onStepSelect !== undefined && typeof onStepSelect !== 'function') {
      throw new TypeError('BaseSteps onStepSelect must be a function.');
    }
    if (typeof messages.label !== 'string' || !messages.label.trim()) {
      throw new TypeError('BaseSteps locale label must be a non-empty string.');
    }
    validateAccessibleName(ariaLabel, ariaLabelledBy);

    const validatedItems = validateItems(items);
    const resolvedStatusLabels = resolveStatusLabels(messages, statusLabels);

    return (
      <ol
        {...componentAttributes}
        {...stepsProps}
        ref={ref}
        role="list"
        aria-label={
          ariaLabel ?? (ariaLabelledBy === undefined ? messages.label : undefined)
        }
        aria-labelledby={ariaLabelledBy}
        data-monkeys-component="steps"
        data-slot="root"
        data-orientation={orientation}
        className={cn(
          theme.slots.stepsRoot,
          orientation === 'horizontal'
            ? theme.slots.stepsHorizontal
            : theme.slots.stepsVertical,
          classNames?.root,
          className,
        )}
        style={{
          ...theme.styles.steps,
          ...style,
        }}
      >
        {validatedItems.map(({ item, encodedId, status }, index) => {
          const itemDomId = `${generatedId}-step-${encodedId}`;
          const labelId = `${itemDomId}-label`;
          const descriptionId = `${itemDomId}-description`;
          const statusId = `${itemDomId}-status`;
          const hasDescription = hasRenderableNode(item.description);
          const statusText =
            status === 'pending' ? undefined : resolvedStatusLabels[status];
          const hasStatusText = Boolean(statusText || item.disabled);
          const interactive =
            onStepSelect !== undefined && item.selectable !== false;
          const describedBy = [
            hasDescription ? descriptionId : undefined,
            hasStatusText ? statusId : undefined,
          ]
            .filter(Boolean)
            .join(' ') || undefined;

          const content = (
            <>
              <span
                aria-hidden="true"
                data-slot="indicator"
                className={cn(
                  theme.slots.stepsIndicator,
                  getIndicatorClassName(status, theme.slots),
                  classNames?.indicator,
                )}
                style={theme.styles.stepsIndicator}
              >
                {getIndicator(status, index)}
              </span>
              <span
                data-slot="content"
                className={cn(theme.slots.stepsContent, classNames?.content)}
              >
                <span
                  id={labelId}
                  data-slot="label"
                  className={cn(theme.slots.stepsLabel, classNames?.label)}
                >
                  {item.label}
                </span>
                {hasDescription ? (
                  <span
                    id={descriptionId}
                    data-slot="description"
                    className={cn(
                      theme.slots.stepsDescription,
                      classNames?.description,
                    )}
                  >
                    {item.description}
                  </span>
                ) : null}
                {hasStatusText ? (
                  <span
                    id={statusId}
                    data-slot="status"
                    className={cn(
                      theme.slots.stepsStatus,
                      classNames?.status,
                    )}
                  >
                    {statusText}
                    {statusText && item.disabled ? '. ' : null}
                    {item.disabled ? resolvedStatusLabels.disabled : null}
                  </span>
                ) : null}
              </span>
            </>
          );

          return (
            <li
              key={item.id}
              id={itemDomId}
              aria-current={status === 'current' ? 'step' : undefined}
              aria-disabled={item.disabled || undefined}
              data-step-id={item.id}
              data-status={status}
              data-disabled={item.disabled || undefined}
              data-selectable={interactive || undefined}
              className={cn(
                theme.slots.stepsItem,
                orientation === 'horizontal'
                  ? theme.slots.stepsItemHorizontal
                  : theme.slots.stepsItemVertical,
                classNames?.item,
              )}
              style={theme.styles.stepsItem}
            >
              {interactive ? (
                <button
                  type="button"
                  disabled={item.disabled}
                  aria-labelledby={labelId}
                  aria-describedby={describedBy}
                  data-slot="trigger"
                  className={cn(
                    theme.slots.stepsTrigger,
                    theme.slots.stepsTriggerInteractive,
                    item.disabled && theme.slots.stepsTriggerDisabled,
                    classNames?.trigger,
                  )}
                  style={theme.styles.stepsTrigger}
                  onClick={() => {
                    if (item.disabled) return;
                    onStepSelect(item.id, item, index);
                  }}
                >
                  {content}
                </button>
              ) : (
                <div
                  data-slot="trigger"
                  className={cn(
                    theme.slots.stepsTrigger,
                    item.disabled && theme.slots.stepsTriggerDisabled,
                    classNames?.trigger,
                  )}
                  style={theme.styles.stepsTrigger}
                >
                  {content}
                </div>
              )}
              {index < validatedItems.length - 1 ? (
                <span
                  aria-hidden="true"
                  data-slot="connector"
                  className={cn(
                    theme.slots.stepsConnector,
                    orientation === 'horizontal'
                      ? theme.slots.stepsConnectorHorizontal
                      : theme.slots.stepsConnectorVertical,
                    status === 'completed' &&
                      theme.slots.stepsConnectorCompleted,
                    status === 'error' && theme.slots.stepsConnectorError,
                    classNames?.connector,
                  )}
                  style={theme.styles.stepsConnector}
                />
              ) : null}
            </li>
          );
        })}
      </ol>
    );
  },
);
